/**
 * Ventas Generales — pantalla unificada de registro, consulta y cancelación.
 *
 * La vista principal es el mantenimiento de ventas generales embebido
 * (ares9620: listado por fechas, cancelar ventas sin pagos y reimprimir).
 * Tarjetas de acción:
 *   - "Registrar venta" (ares9610): ventas misceláneas (GPS, seguros,
 *     servicios) al contado o a crédito, en diálogo a pantalla completa;
 *     al guardar, el listado se recarga.
 *   - "Reportes" (ares9630): ventas, balances a crédito con antigüedad de
 *     saldo y cobros aplicados, en diálogo con barra Volver.
 *
 * Sustituye las entradas de menú de registrarventageneral.js,
 * mantenimientoventasgenerales.js y reportesventasgenerales.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import RegistrarVentaGeneral from "modules/ventasgenerales/registrarventageneral";
import MantenimientoVentasGenerales from "modules/ventasgenerales/mantenimientoventasgenerales";
import ReportesVentasGenerales from "modules/ventasgenerales/reportesventasgenerales";
import useAccionInicial from "lib/useAccionInicial";

export default function VentasGeneralesUnificado() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeRegistrar = tienePermisosModulo(roleDetalles, "ares9610");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares9620");
  const puedeReportes = tienePermisosModulo(roleDetalles, "ares9630");

  const [registroOpen, setRegistroOpen] = useState(false);
  const [reportesOpen, setReportesOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    registrar: { permitido: puedeRegistrar, abrir: () => setRegistroOpen(true) },
    reportes: { permitido: puedeReportes, abrir: () => setReportesOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras registrar una venta).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Ventas Generales"
        subtitle="Registra, consulta y cancela ventas misceláneas en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeRegistrar || puedeReportes) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeRegistrar && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={SellOutlinedIcon}
                  titulo="Registrar venta"
                  descripcion="GPS, seguros y servicios — al contado o a crédito"
                  color={FP.color.success}
                  onClick={() => setRegistroOpen(true)}
                />
              </Grid>
            )}
            {puedeReportes && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={AssessmentOutlinedIcon}
                  titulo="Reportes"
                  descripcion="Ventas, antigüedad de saldo y cobros aplicados"
                  color={FP.color.accentBlue}
                  onClick={() => setReportesOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: mantenimiento de ventas (cancelar/reimprimir) */}
        {puedeMantenimiento && <MantenimientoVentasGenerales embedded refreshToken={refreshKey} />}
      </Box>

      {/* Registrar venta general (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={registroOpen} onClose={() => setRegistroOpen(false)}>
        <RegistrarVentaGeneral embedded onExit={() => { setRegistroOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Reportes (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={reportesOpen} onClose={() => setReportesOpen(false)}>
        <BarraVolver onExit={() => setReportesOpen(false)} />
        <ReportesVentasGenerales />
      </Dialog>
    </Box>
  );
}
